import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "../..";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { userState } from "../../app/reducers/user-slice";
import { UserQueryKey, useGetUserPageQuery } from "../../query/user-query";
import { AppViewMode } from "../../types/user/app-view-mode";
import { Role } from "../../types/user/role";
import { useDebouncedCallback } from "../../utils/custom-hook/use-debounced-callback";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { todayYYYYMMDD } from "../../utils/date/date-format";
import { validateBirth } from "../../utils/validator/birth";
import { ContainedYellowButton } from "../button/ContainedYellowButton";
import { PageController } from "../paging/PageController";
import { BasicSingleSelector } from "../selector/BasicSingleSelector";
import { UserListTable } from "../table/UserListTable";
import { IconTextField } from "../text-field/IconTextField";
import { startCenterLoadingAction } from "../../app/reducers/loading-slice";
import EventBus, { EventTypes } from "../../utils/eventbus/event-bus";
import { ContainedGrayButton } from "../button/ContainedGrayButton";
import { UserExcelUploadDialog } from "../dialog/UserExcelUploadDialog";

interface Props {
  role: Role;
  handleClickDialogOpen: () => void;
  handleClickDialogClose: () => void;
}

const searchTabMap: { [key: string]: string } = {
  name: "이름",
  phone: "전화번호",
  birth: "생년월일",
  appViewMode: "앱 표기",
};

const searchAppViewModeTabMap: { [key: string]: string } = {
  [AppViewMode.Onggoing]: "옹고잉",
  [AppViewMode.Hyundai]: "현대그리팅",
};

const placeholderMap: { [key: string]: string } = {
  name: "프로님 이름을 입력하세요",
  phone: "전화번호를 입력하세요",
  birth: "생년월일을 입력하세요",
};

export const UserTabPanel: React.FC<Props> = ({
  role,
  handleClickDialogOpen,
  handleClickDialogClose,
}) => {
  const dispatch = useAppDispatch();
  const { selectedUser, userPage } = useAppSelector(userState);
  const [payload, setPayload] = useState<any>({});
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [birthValidator, setBirthValidator] = useState(false);
  const [selectedSearchTabStatus, setSelectedSearchTabStatus] =
    useState<string>("name");
  const [excelDialogOpen, setExcelDialogOpen] = useState(false);
  const searchPayload = useAppSelector(state => state.user.userPage);
  useGetUserPageQuery(payload);

  const setSearchPayload = async (page: number = 0) => {
    if (selectedSearchTabStatus === "birth") {
      if (validateBirth(searchKeyword) && searchKeyword !== "") {
        toast.error(`생년월일 형식을 확인해주세요. ex) ${todayYYYYMMDD}`);
        setBirthValidator(true);
        return;
      }
      setBirthValidator(false);
    }

    dispatch(startCenterLoadingAction());
    const newPayload: any = {
      page,
      role,
    };
    if (selectedSearchTabStatus.length > 0 && searchKeyword !== "") {
      newPayload[selectedSearchTabStatus] = searchKeyword;
    }
    await setPayload(newPayload);
    await queryClient.invalidateQueries(UserQueryKey.Page);
  };

  const debouncedSearchPayload = useDebouncedCallback(
    async (page: number = 0) => setSearchPayload(page)
  );

  const handleClickSearchLeftTabItem = (key: string) => {
    setSelectedSearchTabStatus(key);
  };

  const handleClickSearchRightTabItem = (key: string) => {
    setSearchKeyword(key);
  };

  const handleChangeTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newSearchKeyword = event.target.value;
    setSearchKeyword(newSearchKeyword);
  };

  const handleChangePage = (selectedItem: { selected: number }) => {
    const page = selectedItem.selected;
    setSearchPayload(page);
  };

  useDidMountEffect(() => {
    debouncedSearchPayload();
    EventBus.emit(EventTypes.PAGE_CLEAR);
  }, [searchKeyword]);

  useDidMountEffect(() => {
    if (selectedSearchTabStatus === "appViewMode") {
      setSearchKeyword(AppViewMode.Onggoing);
      return;
    }
    setSearchKeyword("");
  }, [selectedSearchTabStatus]);

  useEffect(() => {
    setSearchPayload();
  }, [role]);

  useDidMountEffect(() => {
    if (selectedUser) {
      handleClickDialogOpen();
    } else {
      handleClickDialogClose();
    }
  }, [selectedUser]);

  const handleExcelUpload = () => {
    setExcelDialogOpen(true);
  };

  const handleExcelDialogClose = () => {
    setExcelDialogOpen(false);
  };

  return (
    <>
      <ProUserTabPanelContainer>
        <ProUserTabPanelTitle>
          <PanelTitleLeftContainer>
            <SelectorContainer>
              <BasicSingleSelector
                value={searchTabMap[selectedSearchTabStatus]}
                defaultKey={"이름"}
                candidateMap={searchTabMap}
                onClickSelectItem={handleClickSearchLeftTabItem}
                width="7.063rem"
              />
            </SelectorContainer>
            {selectedSearchTabStatus === "appViewMode" ? (
              <BasicSingleSelector
                value={searchAppViewModeTabMap[searchKeyword]}
                defaultKey={"옹고잉"}
                candidateMap={searchAppViewModeTabMap}
                onClickSelectItem={handleClickSearchRightTabItem}
              />
            ) : (
              <IconTextField
                placeholder={placeholderMap[selectedSearchTabStatus]}
                name={selectedSearchTabStatus}
                value={searchKeyword}
                onChange={handleChangeTextField}
                width="19.438rem"
                isError={birthValidator}
              />
            )}
          </PanelTitleLeftContainer>
          <PanelTitleRightContainer>
            <ContainedGrayButton
              width="13.188rem"
              height="3.188rem"
              text="엑셀로 배송원 등록"
              onClick={handleExcelUpload}
            />
            <ContainedYellowButton
              width="8.188rem"
              height="3.188rem"
              text={"신규 등록"}
              onClick={handleClickDialogOpen}
            />
          </PanelTitleRightContainer>
        </ProUserTabPanelTitle>
        <ProUserTabPanelContent>
          <UserListTable role={role} />
        </ProUserTabPanelContent>
        <ProUserTableContainer>
          <PageController
            basePage={userPage}
            onPageChange={handleChangePage}
          />
        </ProUserTableContainer>
      </ProUserTabPanelContainer>
      {excelDialogOpen && (
        <UserExcelUploadDialog
          handleClickExcelDialogClose={handleExcelDialogClose}
          searchPayload={searchPayload}
        />
      )}
    </>
  );
};

const ProUserTabPanelContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const ProUserTabPanelTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  top: -64px;
  right: 0px;
`;

const PanelTitleLeftContainer = styled.div`
  display: flex;
`;

const PanelTitleRightContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProUserTabPanelContent = styled.div`
  padding-top: 1.25rem;
  flex-grow: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
  min-height: 0;

  overflow-x: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  white-space: nowrap;
`;

const ProUserTableContainer = styled.div`

`;

const SelectorContainer = styled.div`
  margin-right: 0.5rem;
`;
